import Button from '../../components/buttons/Button';
import { useNavigate } from 'react-router-dom';
import ApiService from '../../services/ApiService';
import { useEffect, useState } from 'react';
import { useCookies } from 'react-cookie';
import ModalContent from './ModalContent';

interface Lounge {
  id: string;
  lounge_id: string;
  name: string;
  location: string;
}

interface Transaction {
  id: string;
  booking_id: string;
  transaction_id: string;
  amount: number;
  last_4_digits: string;
  currency: string;
  status: string;
  transaction_date: string;
  payment_method: string;
  cardholder_name: string;
  receipt_url?: string;
  discount_applied?: boolean;
}

interface BoardingPass {
  pnrNo: string;
  boardingPassString: string;
}

interface BoardingPassData {
  boardingPasses: BoardingPass[];
}

interface ParsedBoardingPass {
  pnrNo: string;
  boardingPassArray: string[];
  from: string;
  to: string;
  flightNumber: string;
  seatNumber: string;
  sequenceNumber: string;
  seatClass: string;
}

interface CardDetails {
  bank_name: string;
  network: string;
  bank_code: string;
  type: string;
}

interface Details {
  booking_date: string;
  booking_id: string;
  card_hash: string;
  cardholder_pax: number;
  client_id: string | null;
  complimentary_pax: number;
  external_lounge: string | null;
  guest_pax: number;
  id: string;
  lounge: Lounge;
  lounge_id: string;
  message: string;
  paid_pax: number;
  status: string;
  success: boolean;
  transaction: Transaction;
  transaction_id: string;
  user_id: string | null;
  BoardingPass?: BoardingPassData;
  card_details: CardDetails;
  qr_already_processed: boolean;
}

interface VisitorDetailProps {
  details: Details | null;
  setVisitorDetails: (details: Details | null) => void;
}

interface ErrorType {
  response: {
    data: {
      error: {
        errorMessage: string;
      };
    };
  };
}

const VisitorDetail = ({ details, setVisitorDetails }: VisitorDetailProps) => {
  const navigate = useNavigate();
  const [bookingPNRdetails, setBookingPNRDetails] = useState<
    ParsedBoardingPass[] | undefined
  >();
  const [, setCookies] = useCookies(['transaction_id', 'complimentary_pax']);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const closeModal = () => setIsModalOpen(false);
  const checkInHandler = async () => {
    try {
      await ApiService.post('v1/user/staff/check-in', {
        booking_id: details?.id,
      });

      if (details?.transaction?.transaction_id) {
        setCookies('transaction_id', details.transaction.transaction_id, {
          path: '/',
        });
      }

      if (details?.complimentary_pax) {
        setCookies('complimentary_pax', details.complimentary_pax.toString(), {
          path: '/',
        });
      }

      navigate('/success');
    } catch (error: unknown) {
      console.error('Error: Error in check-in', error);
      const err = error as ErrorType;
      if (err.response && err.response?.data?.error?.errorMessage) {
        setErrorMessage(err?.response?.data?.error?.errorMessage);
        setIsModalOpen(true);
      }
    }
  };

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);

    const formattedDate =
      `${(date.getMonth() + 1).toString().padStart(2, '0')}/` +
      `${date.getDate().toString().padStart(2, '0')}/` +
      `${date.getFullYear().toString().slice(-2)} ` +
      `${date.getHours().toString().padStart(2, '0')}:` +
      `${date.getMinutes().toString().padStart(2, '0')}:` +
      `${date.getSeconds().toString().padStart(2, '0')}`;

    return formattedDate;
  };

  useEffect(() => {
    if (!details?.BoardingPass) return;

    try {
      const data: BoardingPass[] = details.BoardingPass.boardingPasses;

      const splitData: ParsedBoardingPass[] = data.map((item) => {
        const boardingPassArray = item.boardingPassString.trim().split(/\s+/);

        const from = boardingPassArray[2]?.substring(0, 3) || '';
        const to = boardingPassArray[2]?.substring(3, 6) || '';
        const flightNumber = boardingPassArray[2]
          ? `${boardingPassArray[2].substring(6, 8)} ${
              boardingPassArray[3] || ''
            }`
          : '';

        const seatSequenceString = boardingPassArray[4] || '';
        const seatNumber = seatSequenceString.substring(4, 7);
        const seatClass = seatSequenceString[7] || '';
        const sequenceNumber = seatSequenceString.substring(
          Math.max(0, seatSequenceString.length - 4),
        );

        return {
          pnrNo: item.pnrNo,
          boardingPassArray,
          from,
          to,
          flightNumber,
          seatNumber,
          sequenceNumber,
          seatClass,
        };
      });

      setBookingPNRDetails(splitData);
    } catch (error) {
      console.error('Error parsing boarding pass data:', error);
      setBookingPNRDetails(undefined);
    }
  }, [details]);

  const goBackHandler = () => {
    setVisitorDetails(null);
  };
  return (
    <>
      <hr className="w-full border-[0.5px] border-[#9CA3AF]" />
      <div className="-mt-6 flex flex-col gap-4 xl:gap-9">
        <div className="flex w-full flex-col items-center justify-center gap-3">
          <p
            className={
              !details?.qr_already_processed
                ? 'text-2xl font-semibold text-[#1E429F] 2xl:text-3xl'
                : 'text-2xl font-semibold text-[#D91A1D] 2xl:text-3xl'
            }
          >
            {!details?.qr_already_processed
              ? `Please allow a total of ${details?.complimentary_pax} visitors`
              : 'This QR has already been used!'}
          </p>
        </div>
        <div className="grid w-full grid-cols-2 gap-4 bg-white p-5">
          <div className="flex flex-col items-start gap-3">
            <div className="flex items-start gap-3">
              <p className="text-sm font-medium capitalize xl:text-base 2xl:text-lg">
                {details?.card_details?.bank_name || ''}{' '}
                {`${details?.card_details?.type} Card`} -{' '}
                <span className="font-normal">
                  •••• •••• •••• {details?.transaction?.last_4_digits}
                </span>
              </p>
              {details?.card_details?.network && (
                <span className="rounded-[36px] bg-blue-800 px-2 py-1 text-[11px] font-medium capitalize leading-[16.5px] text-blue-50">
                  {details?.card_details?.network}
                </span>
              )}
            </div>
            <p className="text-sm font-medium xl:text-base 2xl:text-lg">
              Guest Name -{' '}
              <span className="font-normal">
                {details?.transaction?.cardholder_name}
              </span>
            </p>
            <p className="whitespace-nowrap text-sm font-medium xl:text-base 2xl:text-lg">
              Unique ID -{' '}
              <span className="truncate font-normal">
                {details?.transaction?.transaction_id}
              </span>
            </p>
            <p className="text-sm font-medium xl:text-base 2xl:text-lg">
              Date & Time -{' '}
              {details?.booking_date && (
                <span className="font-normal">
                  {formatTimestamp(details.booking_date)}
                </span>
              )}
            </p>
          </div>
          {bookingPNRdetails && bookingPNRdetails.length > 0 && (
            <div className="flex flex-col items-end gap-3">
              <div className="flex items-center gap-3">
                <p className="text-primary cursor-pointer text-sm font-medium xl:text-base 2xl:text-lg">
                  Boarding pass details
                </p>
              </div>
              <p className="text-sm font-medium xl:text-base 2xl:text-lg">
                PNR -{' '}
                <span className="font-normal">
                  {bookingPNRdetails[0].pnrNo}
                </span>
              </p>
              <p className="text-sm font-medium xl:text-base 2xl:text-lg">
                Flight -{' '}
                <span className="font-normal">
                  {bookingPNRdetails[0].flightNumber}{' '}
                  {bookingPNRdetails[0].from} to {bookingPNRdetails[0].to}
                </span>
              </p>
              <p className="text-sm font-medium xl:text-base 2xl:text-lg">
                Class{' '}
                <span className="font-normal">
                  {bookingPNRdetails[0].seatClass} |
                </span>{' '}
                Seat{' '}
                <span className="font-normal">
                  {bookingPNRdetails[0].seatNumber} |
                </span>{' '}
                Seq{' '}
                <span className="font-normal">
                  # {bookingPNRdetails[0].sequenceNumber}
                </span>
              </p>
            </div>
          )}
        </div>
        <div className="mx-auto flex w-[506px] flex-col gap-4 2xl:gap-5">
          <Button
            disabled={details?.qr_already_processed}
            text="Check-in"
            type="primary"
            onClick={checkInHandler}
          />
          <Button text="Back" type="secondary" onClick={goBackHandler} />
        </div>
        {isModalOpen && (
          <div className="fixed inset-0 flex items-center justify-center bg-[rgba(0,0,0,0.85)]">
            <ModalContent
              closeModal={closeModal}
              errorMessage={errorMessage}
              setErrorMessage={setErrorMessage}
              setIsInputOpen={() => {}}
            />
          </div>
        )}
      </div>
    </>
  );
};

export default VisitorDetail;
